import { Box, Flex, Text, useColorModeValue, chakra } from '@chakra-ui/react';
import NextLink from 'next/link';
import React from 'react';

import type { Route } from 'nextjs-routes';

import Skeleton from 'ui/shared/chakra/Skeleton';
import Hint from 'ui/shared/Hint';
import { type IconName } from 'ui/shared/IconSvg';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string;
  hint?: string | React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  period?: '1h' | '24h';
  href?: Route;
  icon?: IconName;
  background?: string;
  height?: string;
  padding?: string | number;
};

const Container = ({ href, children }: { href?: Route; children: React.JSX.Element }) => {
  if (href) {
    return (
      <NextLink href={ href } passHref legacyBehavior>
        { children }
      </NextLink>
    );
  }

  return children;
};

const StatsWidget = ({
  className,
  icon,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  diff,
  diffPeriod = '24h',
  diffFormatted,
  period,
  href,
  background,
  height,
  padding,
}: Props) => {
  const skeletonBgColor = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const cardBg = useColorModeValue('#FFFFFF', '#111111');
  const cardBorder = useColorModeValue('#E9ECEF', '#222222');
  const labelColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const hintColor = useColorModeValue('#616161', '#999999');

  return (
    <Container href={ !isLoading ? href : undefined }>
      <Flex
        className={ className }
        alignItems="center"
        bgColor={ isLoading ? skeletonBgColor : cardBg }
        background={ background }
        height={ height }
        p={ padding || '16px' }
        borderRadius="8px"
        border="1px solid"
        borderColor={ cardBorder }
        justifyContent="space-between"
        columnGap={ 2 }
        { ...(href && !isLoading ? {
          as: 'a',
          href,
        } : {}) }
      >
        <Box w={{ base: '100%', lg: icon ? 'calc(100% - 48px)' : '100%' }} >
          <Skeleton
            isLoaded={ !isLoading }
            color="text_secondary"
            fontSize="14px"
            lineHeight="20px"
            w="fit-content"
            display="flex"
            gap="4px"
          >
            <Text
              color={ labelColor }
              whiteSpace="nowrap"
              textTransform="uppercase"
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight={ 500 }
              fontSize="14px"
            >
              { label } { period && `(${ period })` }
            </Text>
            { typeof hint === 'string' ? (
              <Skeleton isLoaded={ !isLoading } alignSelf="center" borderRadius="base">
                <Hint label={ hint } boxSize={ 4 } color={ hintColor }/>
              </Skeleton>
            ) : hint }
          </Skeleton>
          <Skeleton
            isLoaded={ !isLoading }
            display="flex"
            alignItems="baseline"
            fontWeight={ 500 }
            fontSize="20px"
            lineHeight={ 6 }
            mt="8px"
            color={ valueColor }
            fontFamily="'Oxanium', sans-serif"
          >
            { valuePrefix && <chakra.span whiteSpace="pre">{ valuePrefix }</chakra.span> }
            { typeof value === 'string' ? (
              <TruncatedValue isLoading={ isLoading } value={ value }/>
            ) : (
              value
            ) }
            { valuePostfix && <chakra.span whiteSpace="pre">{ valuePostfix }</chakra.span> }
            { diff && Number(diff) > 0 && (
              <>
                <Text ml={ 2 } mr={ 1 } color="green.500">
                  +{ diffFormatted || Number(diff).toLocaleString() }
                </Text>
                <Text variant="secondary" fontSize="sm">({ diffPeriod })</Text>
              </>
            ) }
          </Skeleton>
        </Box>

      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
